import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import Footer from './Footer'
import Header from './Header'
import { CATEGORIES } from '../config/categories'
import { useJobById, useCategories, useJobs } from '../hooks/useApi'
import { useQuery } from '@tanstack/react-query'
import { Helmet } from 'react-helmet-async'

const JobDetail = ({ source }) => {
  const navigate = useNavigate();
  const { id, jobCode, jobTitle } = useParams();
  const [jobId, setJobId] = useState(id); // Initialize with id if available
  const [loading, setLoading] = useState(true);
  const [jobNotFound, setJobNotFound] = useState(false);

  console.log('Source:', source, 'JobCode:', jobCode, 'JobTitle:', jobTitle, 'ID:', id);

  // Get all jobs to find by code or title
  const { data: allJobs, isLoading: allJobsLoading } = useJobs();
  
  // Use React Query hooks
  const { data: jobData, isLoading: jobLoading, error: jobError } = useJobById(jobId);
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  
  // Fetch companies with React Query
  const fetchCompanies = async () => {
    const response = await fetch('https://ghanish.in/company_listview/');
    if (!response.ok) {
      throw new Error(`Error fetching companies: ${response.status}`);
    }
    return response.json();
  };

  const { data: companies = [], isLoading: companyLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: fetchCompanies,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
    onError: (err) => {
      console.error("Error fetching companies:", err);
    }
  });

  // Find job by job code or title slug
  useEffect(() => {
    if (allJobsLoading || !allJobs) return;

    // If we already have an ID from the URL, use it
    if (id) {
      setJobId(id);
      setLoading(false);
      return;
    }

    let foundJob = null;

    // If we're looking up by job code
    if (source === 'jobCode' && jobCode) {
      console.log('Looking up by job code:', jobCode);
      console.log('Available jobs:', allJobs.map(j => j.job_code));
      
      foundJob = allJobs.find(job => 
        job.job_code && job.job_code.toUpperCase() === jobCode.toUpperCase()
      );
    }
    
    // If we're looking up by job title
    if (source === 'jobTitle' && jobTitle) {
      foundJob = allJobs.find(job => {
        if (!job.title) return false;
        const slugifiedTitle = job.title
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^\w\-]+/g, '')
          .replace(/\-\-+/g, '-')
          .replace(/^-+/, '')
          .replace(/-+$/, '');
        return slugifiedTitle === jobTitle;
      });
    }

    if (foundJob) {
      console.log('Found job:', foundJob);
      setJobId(foundJob.job_id);
    } else {
      // If job not found, we could redirect to a 404 page
      console.error("Job not found");
      setJobNotFound(true);
      // Optional: navigate to 404 or jobs page
      // navigate('/browse-jobs');
    }
    
    setLoading(false);
  }, [allJobs, allJobsLoading, id, jobCode, jobTitle, source, navigate]);
  
  // Combine loading states
  const isLoading = jobLoading || categoriesLoading || loading || allJobsLoading || companyLoading;
  
  // Process job data to match expected format
  const job = jobData ? {
    id: jobData.job_id,
    jobTitle: jobData.title || '',
    jobDescription: jobData.description || '',
    jobPosition: jobData.position || '',
    skills: [],
    jobType: jobData.position || 'Full Time',
    package: jobData.package ? (jobData.package).toString() : '0',
    location: jobData.location || 'Not Specified',
    category: jobData.category ? jobData.category.toString() : '',
    location_name: jobData.location_name || '',
    company_name: jobData.company_name || '',
    category_name: jobData.category_name || '',
    company_id: jobData.company_id || '',
    company_image_url: jobData.company_image_url || '',
    company_logo: jobData.company_logo || '',
   
    image: '',
    job_code: jobData.job_code || ''
  } : null;
  
  // Convert categories data to object
  const categories = categoriesData || {};

  // Get related jobs by category
  const relatedJobsByCategory = React.useMemo(() => {
    if (!job || !allJobs) return [];
    
    // Convert category to number for proper comparison
    const jobCategoryId = Number(job.category);
    
    return allJobs
      .filter(relatedJob => 
        Number(relatedJob.category) === jobCategoryId && 
        Number(relatedJob.job_id) !== Number(jobId)
      )
      .slice(0, 20);
  }, [allJobs, job, jobId]);

  // Get more jobs from the same company
  const relatedJobsByCompany = React.useMemo(() => {
    if (!job || !allJobs) return [];
    
    // Convert company name to lowercase for case-insensitive comparison
    const companyName = job.company_name?.toLowerCase();
    
    return allJobs
      .filter(relatedJob => 
        relatedJob.company_name?.toLowerCase() === companyName && 
        Number(relatedJob.job_id) !== Number(jobId)
      )
      .slice(0, 20);
  }, [allJobs, job, jobId]);

  // Get company details
  const companyDetails = React.useMemo(() => {
    if (!job || !companies.length) return null;
    
    return companies.find(company => 
      company.name.toLowerCase() === job.company_name?.toLowerCase()
    );
  }, [companies, job]);

  // Helper function to create slugs
  const slugify = (str) =>
    str?.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '') || '';

  // Handle clicking on a job card
  const handleJobClick = (jobId, jobTitle, jobCode) => {
    // If job code is available, use the short URL format
    if (jobCode) {
      navigate(`/jobs/${jobCode}`);
      return;
    }
    
    // If job title is available, create a URL-friendly slug
    if (jobTitle) {
      const slugifiedTitle = slugify(jobTitle);
      
      if (slugifiedTitle) {
        navigate(`/jobs/${slugifiedTitle}`);
        return;
      }
    }
    
    // Fallback to the original URL format
    const formattedTitle = jobTitle ? jobTitle.toLowerCase().replace(/\s+/g, '+') : 'job';
    navigate(`/job-detail/${formattedTitle}/${jobId}`);
  };

  // Generate proper meta description for sharing
  const generateMetaDescription = () => {
    if (!job) return 'Job details on Fresher Job Adda';
    
    return `${job.jobTitle} at ${job.company_name}. Package: ${job.package}+ LPA. Location: ${job.location_name}. Apply now!`;
  };

  // Generate canonical URL
  const getCanonicalUrl = () => {
    if (!job) return window.location.href;
    
    // If we have a job code, use that for the canonical URL (preferred shorter URL)
    if (job.job_code) {
      return `${window.location.origin}/jobs/${job.job_code}`;
    }
    
    // Fallback to slugified title
    const formattedTitle = slugify(job.jobTitle);
    return `${window.location.origin}/jobs/${formattedTitle}`;
  };
  
  // Get site URL for absolute URLs
  const siteUrl = window.location.origin;
  const jobUrl = window.location.href;

  // Format job data for structured data
  const getStructuredData = () => {
    if (!job) return {};
    
    return {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": job.jobTitle,
      "description": job.jobDescription || generateMetaDescription(),
      "datePosted": new Date().toISOString(),
      "validThrough": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      "employmentType": job.jobType,
      "hiringOrganization": {
        "@type": "Organization",
        "name": job.company_name,
        "sameAs": siteUrl
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": job.location_name,
          "addressCountry": "IN"
        }
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "INR",
        "value": {
          "@type": "QuantitativeValue",
          "value": job.package,
          "unitText": "YEAR"
        }
      },
      "identifier": {
        "@type": "PropertyValue",
        "name": "Fresher Job Adda Job Code",
        "value": job.job_code
      }
    };
  };

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Loading Job Details | Fresher Job Adda</title>
          <meta name="description" content="Loading job details. Please wait." />
        </Helmet>
        <Header />
        <div className="text-center p-5">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading job details...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (jobError || jobNotFound) {
    return (
      <>
        <Helmet>
          <title>Job Not Found | Fresher Job Adda</title>
          <meta name="description" content="The job you are looking for could not be found." />
          <meta name="robots" content="noindex" />
        </Helmet>
        <Header />
        <div className="text-center p-5">
          <div className="alert alert-warning" role="alert">
            <h4 className="alert-heading">Job Not Found</h4>
            <p>The job you are looking for could not be found. It may have been removed or the URL might be incorrect.</p>
            <hr />
            <p className="mb-0">
              <Link to="/browse-jobs" className="btn btn-success mt-3">Browse All Jobs</Link>
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!job) {
    return (
      <>
        <Helmet>
          <title>Job Not Found | Fresher Job Adda</title>
          <meta name="description" content="The job you are looking for could not be found." />
          <meta name="robots" content="noindex" />
        </Helmet>
        <Header />
        <div className="text-center p-5">
          <div className="alert alert-warning" role="alert">
            <h4 className="alert-heading">Job Not Found</h4>
            <p>The job you are looking for could not be found. It may have been removed or the URL might be incorrect.</p>
            <hr />
            <p className="mb-0">
              <Link to="/browse-jobs" className="btn btn-success mt-3">Browse All Jobs</Link>
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{job.jobTitle} | {job.company_name} | Fresher Job Adda</title>
        
        {/* Basic meta tags */}
        <meta name="description" content={generateMetaDescription()} />
        <link rel="canonical" href={getCanonicalUrl()} />
        
        {/* Open Graph tags for Facebook, LinkedIn */}
        <meta property="og:title" content={`${job.jobTitle} | ${job.company_name}`} />
        <meta property="og:description" content={generateMetaDescription()} />
        <meta property="og:url" content={getCanonicalUrl()} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fresher Job Adda" />
        <meta property="og:image" content={`${siteUrl}/assets/img/share-job.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${job.jobTitle} | ${job.company_name}`} />
        <meta name="twitter:description" content={generateMetaDescription()} />
        <meta name="twitter:image" content={`${siteUrl}/assets/img/share-job.jpg`} />
        
        {/* Additional job-specific metadata */}
        <meta name="keywords" content={`jobs, career, ${job.category_name}, ${job.company_name}, ${job.jobTitle}, ${job.location_name}, ${job.job_code}`} />
        <meta name="robots" content="index, follow" />
        
        {/* Job specific meta tags */}
        <meta name="job:title" content={job.jobTitle} />
        <meta name="job:company" content={job.company_name} />
        <meta name="job:location" content={job.location_name} />
        <meta name="job:salary" content={`${job.package}+ LPA`} />
        <meta name="job:code" content={job.job_code} />
        <meta name="job:category" content={job.category_name} />
        <meta name="job:position" content={job.jobPosition} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(getStructuredData())}
        </script>
      </Helmet>
      
      <Header />
      <div className="page-title">
        <div className="container">
          <div className="page-caption">
            <h2>{job.jobTitle}</h2>
            <p>
              <Link to="/">Home</Link> <i className="fa fa-angle-double-right"></i> 
              <Link to="/browse-jobs">Jobs</Link> <i className="fa fa-angle-double-right"></i>
              {job.job_code ? (
                <span> {job.jobTitle}</span>
              ) : (
                <span>{job.jobTitle}</span>
              )}
            </p>
          </div>
        </div>
      </div>

      <section className="padd-top-80 padd-bot-60" style={{backgroundColor:'#f8f9fa'}}>
        <div className="container">
          <div className="row">
          {/* <div className="col-12 mb-3">
  <img 
    src={job.company_image_url && job.company_image_url.startsWith('data:') 
      ? job.company_image_url 
      : "https://media.istockphoto.com/id/178447404/photo/modern-business-buildings.jpg?s=612x612&w=0&k=20&c=MOG9lvRz7WjsVyW3IiQ0srEzpaBPDcc7qxYsBCvAUJs="}
    alt={job.company_name || "Company Logo"} 
    className="card-img-top"
    style={{ width: "100%", height: "200px", objectFit: "cover" }} 
  />
</div> */}

            <div className="col-md-8 col-sm-7">
              {/* Company Image and Basic Job Info */}
              <div className="card mb-4 border-0 shadow-sm">
                <div className="row g-0">
                  <div className="col-md-12">
                    <div className="card-body">
                      <div className="company-image-container mb-4" style={{ position: 'relative', height: '200px', overflow: 'hidden' }}>
                        <img 
                          src={job.company_image_url || "https://media.istockphoto.com/id/178447404/photo/modern-business-buildings.jpg?s=612x612&w=0&k=20&c=MOG9lvRz7WjsVyW3IiQ0srEzpaBPDcc7qxYsBCvAUJs="}
                          alt={job.company_name}
                          className="company-image"
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                          onError={(e) => {
                            e.target.src = "https://media.istockphoto.com/id/178447404/photo/modern-business-buildings.jpg?s=612x612&w=0&k=20&c=MOG9lvRz7WjsVyW3IiQ0srEzpaBPDcc7qxYsBCvAUJs=";
                          }}
                        />
                      </div>
                      <h3 className="card-title">{job.jobTitle}</h3>
                      <h5 className="text-muted">{job.company_name}</h5>
                      
                      <div className="d-flex flex-wrap mt-3">
                        <div className="me-4 mb-2">
                          <i className="fas fa-wallet me-2 text-success"></i>
                          {job.package}+ LPA
                        </div>
                        <div className="me-4 mb-2">
                          <i className="fas fa-briefcase me-2 text-success"></i>
                          {job.jobPosition}
                        </div>
                        <div className="me-4 mb-2">
                          <i className="fas fa-map-marker-alt me-2 text-success"></i>
                          {job.location_name}
                        </div>
                        <div className="mb-2">
                          <i className="fas fa-list me-2 text-success"></i>
                          {job.category_name}
                        </div>
                      </div>
                      
                    
                    </div>
                  </div>
                </div>
              </div>

              {/* Job Description */}
              <div className="detail-wrapper mb-4">
                <div className="detail-wrapper-header">
                  <h4>Job Description</h4>
                </div>
                <div className="detail-wrapper-body">
                  <p>{job.jobDescription || "No description available"}</p>
                </div>
              </div>
              
              {/* Only show HR tag and Category related jobs section if there are related jobs */}
              {relatedJobsByCategory.length > 0 && (
                <>
                  <hr className="my-5" />
                  
                  <div className="mb-4">
                    <h4 className="mb-4">More {job.category_name} Jobs</h4>
                    <div className="row">
                      {relatedJobsByCategory.slice(0, 20).map((relatedJob) => (
                        <div className="col-md-6 mb-4" key={relatedJob.job_id}>
                          <div 
                            className="card h-100 shadow-sm border-0 hover-shadow"
                            onClick={() => handleJobClick(
                              relatedJob.job_id, 
                              relatedJob.title, 
                              relatedJob.job_code
                            )}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="card-body">
                              <div className="d-flex justify-content-between mb-3">
                                <h6 className="card-title mb-1 text-truncate" style={{ maxWidth: "80%" }}>
                                  {relatedJob.title}
                                </h6>
                                <span className="badge bg-success">
                                  {relatedJob.category_name}
                                </span>
                              </div>
                              
                              <div className="mb-3">
                                <p className="text-muted mb-1 small">
                                  <i className="fa fa-briefcase me-2"></i> {relatedJob.position || 'Not specified'}
                                </p>
                                <p className="text-muted mb-1 small">
                                  <i className="fas fa-wallet me-2"></i> {relatedJob.package || '0'}+ LPA
                                </p>
                                <p className="text-muted mb-0 small">
                                  <i className="fas fa-map-marker-alt me-2"></i> {relatedJob.location_name || 'Not specified'}
                                </p>
                                <p className="text-muted mb-0 small">
                                  <i className="fas fa-building me-2"></i> {relatedJob.company_name || 'Not specified'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {relatedJobsByCategory.length > 20 && (
                      <div className="text-center mt-3">
                        <Link to={`/category/${slugify(job.category_name)}`} className="btn btn-outline-success">
                          View All {job.category_name} Jobs
                        </Link>
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {/* Only show HR tag and Company related jobs section if there are related jobs */}
              {relatedJobsByCompany.length > 0 && (
                <>
                  <hr className="my-5" />
                  
                  <div>
                    <h4 className="mb-4">More Jobs at {job.company_name} </h4>
                    <div className="row">
                      {relatedJobsByCompany.slice(0, 20).map((relatedJob) => (
                        <div className="col-md-6 mb-4" key={relatedJob.job_id}>
                          <div 
                            className="card h-100 shadow-sm border-0 hover-shadow"
                            onClick={() => handleJobClick(
                              relatedJob.job_id, 
                              relatedJob.title, 
                              relatedJob.job_code
                            )}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="card-body">
                              <div className="d-flex justify-content-between mb-3">
                                <h6 className="card-title mb-1 text-truncate" style={{ maxWidth: "80%" }}>
                                  {relatedJob.title}
                                </h6>
                                <span className="badge bg-success">
                                  {relatedJob.category_name}
                                </span>
                              </div>
                              
                              <div className="mb-3">
                                <p className="text-muted mb-1 small">
                                  <i className="fa fa-briefcase me-2"></i> {relatedJob.position || 'Not specified'}
                                </p>
                                <p className="text-muted mb-1 small">
                                  <i className="fas fa-wallet me-2"></i> {relatedJob.package || '0'}+ LPA
                                </p>
                                <p className="text-muted mb-0 small">
                                  <i className="fas fa-map-marker-alt me-2"></i> {relatedJob.location_name || 'Not specified'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {relatedJobsByCompany.length > 20 && (
                      <div className="text-center mt-3">
                        <Link to={`/company/${slugify(job.company_name)}`} className="btn btn-outline-success">
                          View All Jobs at {job.company_name}
                        </Link>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="col-md-4 col-sm-5" >
              {/* Company Card */}
              <Link 
                className="card mb-4 border-0 shadow-sm text-center"
                to={`/company/${slugify(job.company_name)}`} 
              >
                <div className="company-image-container" style={{ position: 'relative', height: '150px', overflow: 'hidden' }}>
                  <img 
                    src={job.company_image_url || "https://media.istockphoto.com/id/178447404/photo/modern-business-buildings.jpg?s=612x612&w=0&k=20&c=MOG9lvRz7WjsVyW3IiQ0srEzpaBPDcc7qxYsBCvAUJs="}
                    alt={job.company_name}
                    className="company-image"
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.src = "https://media.istockphoto.com/id/178447404/photo/modern-business-buildings.jpg?s=612x612&w=0&k=20&c=MOG9lvRz7WjsVyW3IiQ0srEzpaBPDcc7qxYsBCvAUJs=";
                    }}
                  />
                </div>
                <div className="card-body">
                  <h5 className="card-title mb-0">{job.company_name}</h5>
                </div>
              </Link>

            
              {/* Job Details */}
              <div className="widget-boxed mb-4">
                <div className="widget-boxed-header">
                  <h4>
                    <i className="fas fa-info-circle me-2"></i>
                    Job Details
                  </h4>
                </div>
                <div className="widget-boxed-body">
                  <div className="side-list no-border">
                    <ul className="list-unstyled">
                      <li className="mb-3">
                        <i className="fas fa-wallet me-2 text-success"></i>
                        Package: {job.package}+ LPA
                      </li>
                      <li className="mb-3">
                        <i className="fas fa-briefcase me-2 text-success"></i>
                        Position: {job.jobPosition}
                      </li>
                      <li className="mb-3">
                        <i className="fas fa-list me-2 text-success"></i>
                        Category: {categories[job.category] || job.category}
                      </li>
                      <li className="mb-3">
                        <i className="fas fa-map-marker-alt me-2 text-success"></i>
                        Location: {job.location_name}
                      </li>
                      <li className="mb-3">
                        <i className="fas fa-code me-2 text-success"></i>
                        Job Code: {job.job_code}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Share links */}
              <div className="widget-boxed">
                <div className="widget-boxed-header">
                  <h4>
                    <i className="fas fa-share-alt me-2"></i>
                    Share Job
                  </h4>
                </div>
                <div className="widget-boxed-body">
                  <div className="side-list no-border">
                    <ul className="social-links list-unstyled">
                      <li className="mb-2">
                        <a 
                          href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary mb-2 w-100"
                        >
                          <i className="fab fa-facebook-f me-2"></i> Facebook
                        </a>
                      </li>
                      <li className="mb-2">
                        <a 
                          href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${encodeURIComponent(`Check out this job: ${job.jobTitle}`)}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-outline-info mb-2 w-100"
                        >
                          <i className="fab fa-twitter me-2"></i> Twitter
                        </a>
                      </li>
                      <li className="mb-2">
                        <a 
                          href={`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary mb-2 w-100"
                        >
                          <i className="fab fa-linkedin-in me-2"></i> LinkedIn
                        </a>
                      </li>
                      <li>
                        <a 
                          href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out this job: ${job.jobTitle} ${window.location.href}`)}`}
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn btn-outline-success w-100"
                        >
                          <i className="fab fa-whatsapp me-2"></i> WhatsApp
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default JobDetail;